import { ArrowForwardIosSharp } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, styled as s } from '@mui/material';
import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useSelector } from 'react-redux';

import ProjectLink from './ProjectLink';
import { Parent, Tree } from '@/components/ui/Tree/CustomAccordion';
import { getColorStatus } from '@/helper/getColorStatus';
import { getStatus } from '@/helper/getStatus';

const MuiAccordionSummary = s((props) => <AccordionSummary expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />} {...props} />)(
	({ theme }) => ({
		backgroundColor: 'rgba(0, 0, 0, .03)',
		flexDirection: 'row-reverse',
		'& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
			transform: 'rotate(90deg)',
		},
		'& .MuiAccordionSummary-content': {
			marginLeft: theme.spacing(1),
		},
		...theme.applyStyles('dark', {
			backgroundColor: 'rgba(255, 255, 255, .05)',
		}),
	})
);

const classList = ['.btn-action', '.MuiBackdrop-root'];

// eslint-disable-next-line react/display-name
const ProjectRow = React.memo(({ project, idx = 0, currOpen = false, lastChild = false, setLastChild = () => {}, type = 'child' }) => {
	const pStatus = useSelector((state) => state.content.projectStatusD);
	const [open, setOpen] = useState(false);
	const [lastChildOpen, setLastChildOpen] = useState(false);
	const a = idx > 0 ? 0.5 : 0;

	const icon = project.productprefix_detail ? project.productprefix_detail[0]?.icon : project.component_prefix_icon;
	const isIcon = icon && icon !== 'No icon';
	const pType = project.productprefix_detail ? project.productprefix_detail[0]?.productprefix : project.component_prefix_name;
	// const pTypeIc = project.producttype_detail ? project.producttype_detail[0]?.icon : project.component_producttype_icon;
	const pTypeS = project.producttype >= 0 ? project.producttype || 0 : project.component_producttype_id - 1 || 0;
	const pPType = project.status || project.component_status || 0;
	const pNumber = project.productcodenumber || project.component_code_number;
	const pName = project.productname || project.component_productname;
	const pVer = project.productversion || project.component_productversion;

	if (!pNumber && !pType) return null;
	const isChild = type === 'child';
	const isSearch = type === 'search';
	const pRel = isChild ? project.projectrelations : isSearch ? project.projectrelations : project.parent_projectrelations;
	const rel = pRel?.length;
	let lastRel = 0;
	if (idx === 0 && rel) {
		if (isChild) {
			lastRel = pRel[rel - 1]?.projectrelations?.length || 0;
		} else if (!isSearch) {
			lastRel = pRel[rel - 1]?.parent_projectrelations?.length || 0;
		} else {
			lastRel = pRel[rel - 1]?.projectrelations?.length || 0;
		}
	}
	const deduction = lastChildOpen && lastRel ? lastRel * 64 : 0;
	// const isPTypeIc = pTypeIc && pTypeIc !== 'No icon'
	// const pTypeC = isPTypeIc ? <img src={pTypeIc} className='w-7 h-7' /> : '-'
	const color = getColorStatus(pTypeS);
	const status = getStatus(pStatus, pPType);
	const pStat =
		status.url && status.url !== 'No icon' ? (
			<div className='flex h-8 m-auto'>
				<img className='m-auto' title={status.title} src={status.url} alt={status.title} />
			</div>
		) : (
			<div className='flex h-8'>{status.title}</div>
		);
	const pTypeStatus = <div className={`flex m-auto h-4 w-4 rounded-full`} style={{ background: color.hex }} title={color.name}></div>;
	return rel > 0 ? (
		<Accordion
			className={twMerge('w-full !bg-white', open && lastChild ? 'acc-last' : '')}
			defaultExpanded={idx === 0}
			expanded={idx === 0 ? true : open}
			onChange={(e) => {
				const isAction =
					e.target?.matches(classList) || e.target?.closest('button')?.matches(classList) || e.target.closest('.MuiModal-root');
				if (isAction) return;
				const status = !open;
				if (lastChild) {
					setLastChild(status);
				}
				setOpen(status);
			}}
		>
			<MuiAccordionSummary
				expandIcon={<ArrowForwardIosSharp fontSize='12' />}
				className={twMerge('w-full border-y !bg-red')}
				sx={{
					'&.MuiAccordionSummary-root': {
						borderTop: '1px solid hsl(var(--border))',
						backgroundColor: 'white',
					},
					'& > .MuiAccordionSummary-expandIconWrapper': {
						position: 'absolute',
						left: `${0.565 + a * idx + idx}rem`,
					},
				}}
			>
				<div className='grid w-full grid-cols-12'>
					<Tree
						size={idx === 0 ? 0 : 1}
						h={currOpen ? rel * 4 : 0}
						l={0.5 + a * idx + idx}
						style={{ paddingLeft: `${2 + a * idx + idx}rem` }}
						className={twMerge(`relative col-span-2 flex items-center gap-2`, `pl-12`)}
						open={currOpen}
						last_child={lastChild}
						last_child_open={lastChildOpen}
						type='project'
					>
						{isIcon && <img src={icon} className='w-7 h-7' />}
						<span className='select-text'>{pType}</span>
					</Tree>
					<div className='col-span-1 select-text'>{pNumber}</div>
					<div className='col-span-3 select-text'>{pName}</div>
					<div className='col-span-1 text-center select-text'>{pVer}</div>
					<div className='flex justify-center col-span-1 text-center'>{pTypeStatus}</div>
					<div className='flex justify-center col-span-1 text-center'>{pStat}</div>
					<div className='col-span-3 text-center'>
						<ProjectLink project={project} type={type} />
					</div>
				</div>
			</MuiAccordionSummary>
			<AccordionDetails className=''>
				<Parent
					className={twMerge('w-full h-full', open ? 'open' : '', `${idx}`)}
					size={idx === 0 ? 0 : 1}
					// h={(idx === 0 ? true : open) ? 'calc(100% - 44px - 32px)' : 0}
					// h={'calc(100% - 44px - 32px)'}
					open={idx === 0 || open}
					// h={`${idx === 0 ? `calc(100% - 44px - 32px)` : `${rel ? rel * 64 - 12 : 64}px`}`}
					h={`${idx === 0 ? `calc(100% - 44px - 32px - ${lastChildOpen ? deduction : 0}px)` : `${rel ? rel * 64 - 12 : 64}px`}`}
					l={0.5 + a * idx + idx}
					last_child_open={lastChildOpen}
					last_child={idx === 0}
					type='project'
				>
					{rel > 0 &&
						pRel.map((p, i) => (
							<ProjectRow
								key={JSON.stringify(p)}
								project={p}
								idx={idx + 1}
								open={idx === 0 || open}
								lastChild={i + 1 === +rel}
								setLastChild={setLastChildOpen}
								type={type}
							/>
						))}
				</Parent>
			</AccordionDetails>
		</Accordion>
	) : (
		<div className='grid items-center w-full h-16 grid-cols-12 bg-white border-y'>
			<Tree
				size={!rel && idx === 0 ? 0 : 2}
				l={0.5 + a * idx + idx}
				style={{ paddingLeft: `${2 + (a * idx + idx)}rem` }}
				className={twMerge(`relative col-span-2 flex items-center gap-2`)}
				type='project'
			>
				{isIcon && <img src={icon} className='w-7 h-7' />}
				<span className='select-text'>{pType}</span>
			</Tree>
			<div className='col-span-1 select-text'>{pNumber}</div>
			<div className='col-span-3 select-text'>{pName}</div>
			<div className='col-span-1 text-center select-text'>{pVer}</div>
			<div className='flex justify-center col-span-1 text-center'>{pTypeStatus}</div>
			<div className='flex justify-center col-span-1 text-center'>{pStat}</div>
			<div className='col-span-3 text-center'>
				<ProjectLink project={project} type={type} />
			</div>
		</div>
	);
});
export default ProjectRow;
